import {
  useRef,
  type FunctionComponent,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent,
  type Touch,
  useState,
  useMemo,
} from 'react';
import { VStack } from '@chakra-ui/react';
import cx from 'classnames';
import { first } from 'lodash';
import ScrubberTimeline from './components/ScrubberTimeline';
import ScrubberControls from './components/ScrubberControls';
import ScrubberHeaderSection from './components/ScrubberHeaderSection';
import { secondInPixels } from './constants';

import { Timestamp } from '../../services/Timestamp';
import { useScrubberData } from '../../hooks/useScrubberData';
import { getEventFactory } from '../../utils/get-event-factory';
import { Event, type TargetVessel, type VesselPosition } from '../../types';
import { useAppInsights } from '../../hooks/useAppInsights';
import { type OperationViewModel } from '../Map/types';

interface Props {
  targetVessel: TargetVessel;
  setTimestamp: (timestamp: Timestamp) => void;
  setStartTimestamp: (timestamp: Timestamp) => void;
  timestamp: Timestamp;
  isTripsPanelOpen: boolean;
  isDraggingScrubber: boolean;
  setIsDraggingScrubber: (isDragging: boolean) => void;
  vesselPositions: VesselPosition[];
  operation: OperationViewModel;
  switchToLiveview: () => void;
  onTargetLock: (mmsi: string) => void;
  jobStartTimestamp: Timestamp;
}

const Scrubber: FunctionComponent<Props> = ({
  isTripsPanelOpen,
  timestamp,
  setTimestamp,
  setStartTimestamp,
  isDraggingScrubber,
  setIsDraggingScrubber,
  targetVessel,
  vesselPositions,
  operation,
  switchToLiveview,
  onTargetLock,
  jobStartTimestamp,
}) => {
  const scrubberData = useScrubberData(targetVessel?.mmsi);
  const [isPlaying, setIsPlaying] = useState(false);
  const initialScrubTime = useRef(timestamp);
  const previousTouch = useRef<Touch>(null);
  const previousMouseX = useRef(0);
  const appInsights = useAppInsights();

  const isMouseEvent = (e: any): boolean => {
    const event = e as ReactMouseEvent;
    return event && typeof event.clientX === 'number';
  };

  const handleMouseDragStart = (e: ReactMouseEvent): void => {
    previousMouseX.current = e.clientX;
  };

  const handleTouchDragStart = (e: TouchEvent): void => {
    previousTouch.current = first(e.touches);
  };

  const handleDragStart = (e: ReactMouseEvent | TouchEvent): void => {
    if (isMouseEvent(e)) {
      handleMouseDragStart(e as ReactMouseEvent);
    } else {
      handleTouchDragStart(e as TouchEvent);
    }
    initialScrubTime.current = timestamp;
    setIsDraggingScrubber(true);
  };

  const getMouseMovementDelta = (e: ReactMouseEvent): number => {
    if (!previousMouseX.current) {
      return 0;
    }

    return previousMouseX.current - e.clientX;
  };

  const getTouchMovementDelta = (e: TouchEvent): number => {
    if (!previousTouch.current) {
      return 0;
    }

    const touch = first(e.touches);
    return previousTouch.current.pageX - touch.pageX;
  };

  const handleMove = (e: ReactMouseEvent | TouchEvent): void => {
    if (!isDraggingScrubber) {
      return;
    }

    const movementDelta = isMouseEvent(e)
      ? getMouseMovementDelta(e as ReactMouseEvent)
      : getTouchMovementDelta(e as TouchEvent);

    if (movementDelta === 0) {
      return;
    }

    const timeDelta = movementDelta / secondInPixels;
    const newScrubTimestamp = initialScrubTime.current.toSeconds() + timeDelta;
    setTimestamp(Timestamp.fromSeconds(newScrubTimestamp));
  };

  const handleDragEnd = (): void => {
    setIsDraggingScrubber(false);
    const event = getEventFactory(Event.TimelineScrubbed)(
      initialScrubTime.current,
      timestamp,
      window.location.href,
      targetVessel?.mmsi,
      targetVessel?.name
    );
    appInsights.trackAnalyticsEvent(event);
  };

  const speed = useMemo(() => {
    return vesselPositions?.find(x => x.mmsi === targetVessel?.mmsi)?.speed;
  }, [vesselPositions, targetVessel]);

  const scrubberClasses = cx('scrubber-container', {
    shrunk: isTripsPanelOpen,
  });

  return (
    <VStack className={scrubberClasses}>
      <ScrubberControls
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        vessel={targetVessel}
        timestamp={timestamp}
        timezone={operation.timezone}
        setTimestamp={setTimestamp}
        switchToLiveview={switchToLiveview}
        isLockedOnTarget={targetVessel?.isLockedOnTarget}
        onTargetLock={onTargetLock}
        jobStartTimestamp={jobStartTimestamp}
        isTripsPanelOpen={isTripsPanelOpen}
        operation={operation.key}
        setStartTimestamp={setStartTimestamp}
      />
      <ScrubberHeaderSection timestamp={timestamp} vessel={targetVessel} speed={speed} timezone={operation.timezone} />
      <ScrubberTimeline
        currentTimestamp={timestamp}
        isTripsPanelOpen={isTripsPanelOpen}
        data={scrubberData}
        isDraggingScrubber={isDraggingScrubber}
        timezone={operation.timezone}
        vessel={targetVessel}
        handleDragEnd={handleDragEnd}
        handleDragStart={handleDragStart}
        handleMove={handleMove}
      />
    </VStack>
  );
};

export default Scrubber;
